import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, Spinner, Button } from '@klaro7/ui';

import { useWalletBonuses } from '@/features/wallet/hooks/useWallet';
import type { WalletBonusSource } from '@/features/wallet/services/wallet.service';
import { useMyReferral } from '@/features/referrals/hooks/useReferral';

const bonusSourceLabels: Record<WalletBonusSource, string> = {
  welcome: 'Bonus Benvenuto',
  recharge_package: 'Bonus Ricarica',
  first_recharge_promo: 'Promo Prima Ricarica',
  cashback: 'Cashback',
  birthday: 'Bonus Compleanno',
  referral_referrer: 'Porta un Amico',
  referral_referee: 'Bonus Invito',
  legacy: 'Bonus',
  manual: 'Bonus',
};

function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'Senza scadenza';
  const formatted = new Date(expiresAt).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `In scadenza il ${formatted}`;
}

export function PromozioniPage() {
  const { data: bonuses, isLoading } = useWalletBonuses();
  const { data: referral } = useMyReferral();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!referral?.shareLink) return;
    try {
      await navigator.clipboard.writeText(referral.shareLink);
      setCopied(true);
      toast.success('Link copiato negli appunti');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Impossibile copiare il link');
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Le mie Promozioni</h1>
          <p className="text-neutral-500">I tuoi bonus attivi e il tuo codice invito</p>
        </div>

        {/* Active bonuses */}
        <Card className="border-neutral-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-neutral-900">Bonus attivi</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : !bonuses?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-neutral-100 p-4">
                  <svg className="h-8 w-8 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <p className="font-medium text-neutral-600">Nessun bonus attivo</p>
                <p className="mt-1 text-sm text-neutral-400">
                  Ricarica il tuo borsellino per ricevere bonus e promozioni
                </p>
                <Link
                  to="/my/wallet"
                  className="mt-3 inline-block text-sm font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal-400"
                >
                  Vai al borsellino
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {bonuses.map((bonus) => (
                  <div
                    key={bonus.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 p-4 transition-colors hover:bg-neutral-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {bonusSourceLabels[bonus.source] ?? 'Bonus'}
                        </p>
                        <p className="text-xs text-neutral-500">{formatExpiry(bonus.expiresAt)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-brand-orange">
                        € {bonus.remaining.toFixed(2)}
                      </p>
                      {bonus.remaining < bonus.amount && (
                        <p className="text-[11px] text-neutral-400">
                          di € {bonus.amount.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral — Porta un amico */}
        <Card className="border-neutral-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-neutral-900">Porta un amico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-neutral-500">
              Condividi il tuo link: tu e il tuo amico riceverete un bonus quando si registra.
            </p>

            {referral?.referralCode && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-neutral-500">Il tuo codice:</span>
                <span className="rounded-md bg-brand-teal/10 px-2.5 py-1 text-sm font-semibold tracking-wide text-brand-teal">
                  {referral.referralCode}
                </span>
                {typeof referral.referredCount === 'number' && referral.referredCount > 0 && (
                  <span className="text-xs text-neutral-400">
                    {referral.referredCount} amici invitati
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                readOnly
                value={referral?.shareLink ?? ''}
                placeholder="Caricamento link…"
                className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700 outline-none"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                variant="primary"
                className="shrink-0 bg-brand-orange hover:bg-brand-orange/90"
                onClick={handleCopy}
                disabled={!referral?.shareLink}
              >
                {copied ? 'Copiato!' : 'Copia link'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
